import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, FileSearch, Check, X, Ban } from 'lucide-react';
import { staffMovementApi } from '../../api/staff-movement';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { ReturnAction } from '../../components/ReturnAction';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader, RecordHeaderField } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { STAFF_MOVEMENT_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  promotion: 'Promotion', transfer: 'Transfer', position_change: 'Position Change', department_change: 'Department Change',
  duty_station_change: 'Duty Station Change', supervisor_change: 'Supervisor Change', grade_step_change: 'Grade/Step Change',
  salary_adjustment: 'Salary Adjustment', temporary_assignment: 'Temporary Assignment', other: 'Other',
};
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  approved: 'Approved', approved_pending_effective: 'Approved - Pending Effective Date', implemented: 'Implemented', rejected: 'Rejected', cancelled: 'Cancelled',
};

export function StaffMovementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movementId = Number(id);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canApprove = usePermission('staff_movement.approve');
  const canViewSalary = usePermission('compensation.view_history');

  const movementQuery = useQuery({ queryKey: ['staff-movement', movementId], queryFn: () => staffMovementApi.getById(movementId) });
  const docsQuery = useQuery({ queryKey: ['staff-movement-documents', movementId], queryFn: () => genericDocumentApi.listFor('staff_movement', movementId) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff-movement', movementId] });

  const submitMutation = useMutation({ mutationFn: () => staffMovementApi.submit(movementId), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const reviewMutation = useMutation({ mutationFn: () => staffMovementApi.supervisorReview(movementId), onSuccess: () => { showSuccess('Moved to HR review.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decideMutation = useMutation({
    mutationFn: ({ decision, comment }: { decision: 'approved' | 'rejected'; comment: string }) => staffMovementApi.decide(movementId, decision, comment),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => staffMovementApi.cancel(movementId),
    onSuccess: () => { showSuccess('Cancelled.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  if (!movementQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const r = movementQuery.data.data;

  const fields: RecordHeaderField[] = [
    { label: 'Movement type', value: MOVEMENT_TYPE_LABELS[r.movement_type] ?? r.movement_type },
    { label: 'Effective date', value: formatDate(r.effective_date) },
    { label: 'Current position', value: r.current_position_name ?? '-' },
    { label: 'Proposed position', value: r.proposed_position_name ?? '-' },
    { label: 'Current department', value: r.current_department_name ?? '-' },
    { label: 'Proposed department', value: r.proposed_department_name ?? '-' },
    { label: 'Current grade', value: r.current_grade ?? '-' },
    { label: 'Proposed grade', value: r.proposed_grade ?? '-' },
    ...(canViewSalary ? [
      { label: 'Current salary', value: r.current_salary ? Number(r.current_salary).toLocaleString() : '-' },
      { label: 'Proposed salary', value: r.proposed_salary ? `${r.proposed_currency ?? 'NGN'} ${Number(r.proposed_salary).toLocaleString()}` : '-' },
    ] : []),
    { label: 'Promotion basis', value: r.promotion_basis ?? '-' },
    { label: 'Temporary assignment', value: r.is_temporary ? `Yes, until ${r.temporary_end_date ?? '-'}` : 'No' },
  ];

  return (
    <div className="page">
      <Link to="/hr/staff-movement">&larr; Back to Staff Movement</Link>
      <RecordHeader
        title={r.movement_reference}
        entityType="staff_movement"
        entityId={movementId}
        fields={fields}
        actions={
          <span className="row-actions">
            {r.status === 'draft' && <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}><Send size={16} className="inline-icon" /> Submit</button>}
            {r.status === 'submitted' && <button className="btn-primary" onClick={() => reviewMutation.mutate()} disabled={reviewMutation.isPending}><FileSearch size={16} className="inline-icon" /> Move to HR Review</button>}
            {r.status === 'under_review' && canApprove && (
              <>
                <button className="btn-primary" onClick={() => { const c = window.prompt('Approval comment:'); if (c) decideMutation.mutate({ decision: 'approved', comment: c }); }}><Check size={16} className="inline-icon" /> Approve</button>
                <button className="button-danger" onClick={() => { const c = window.prompt('Rejection reason:'); if (c) decideMutation.mutate({ decision: 'rejected', comment: c }); }}><X size={16} className="inline-icon" /> Reject</button>
                <ReturnAction onReturn={(comment) => staffMovementApi.return(movementId, comment)} queryKeysToInvalidate={[['staff-movement', movementId]]} />
              </>
            )}
            {['draft', 'submitted', 'under_review', 'approved', 'approved_pending_effective'].includes(r.status) && (
              <button className="button-danger" onClick={() => { if (window.confirm('Cancel this movement request?')) cancelMutation.mutate(); }}><Ban size={16} className="inline-icon" /> Cancel</button>
            )}
            <button className="btn-secondary" onClick={() => navigate(`/hr/staff/${r.employee_id}`)}>View Employee</button>
          </span>
        }
        readOnly={!canApprove}
      />
      <p className="muted">{r.employee_name} · Status: <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  {r.reason && (<><h3>Reason</h3><p>{r.reason}</p></>)}
                  <h3>Detailed Justification</h3>
                  <p>{r.justification}</p>
                  {r.handover_requirements && (<><h3>Handover Requirements</h3><p>{r.handover_requirements}</p></>)}
                  {r.return_comment && <p className="muted">Return comment: {r.return_comment}</p>}
                  {r.decision_comment && <p className="muted">Decision comment: {r.decision_comment}</p>}
                </section>
                <CommentsThread entityType="staff_movement" entityId={movementId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('staff_movement', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('staff_movement', movementId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('staff_movement', documentId)}
                queryKeyToInvalidate={['staff-movement-documents', movementId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="staff_movement" entityId={movementId} actionStageMap={STAFF_MOVEMENT_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
